import { ChangeEvent, SyntheticEvent, useRef, useState } from "react";
import { useAppDispatch } from "../../../app";
import { addBlurredEntity, changeEditing } from "../../../app/store/appSlice";
import { isEmpty } from "../../utilities/value";
import { GroupBlockEntityType } from "../../../configMap";
import useAnnotations from "../../../annotation/hooks/useAnnotations";
import useKeyEvent from "../../../configMap/hooks/useKeyEvent";
import { Keys } from "../../constants";

interface UseEditEntityTextProps {
  entityType: GroupBlockEntityType;
  documentId: string;
  annotationId: string;
  defaultValue: string;
  groupBlockIndex: number;
  isTableMode: boolean;
}

const useEditEntityText = ({
  entityType,
  documentId,
  annotationId,
  defaultValue,
  groupBlockIndex,
  isTableMode,
}: UseEditEntityTextProps) => {
  const dispatch = useAppDispatch();

  const isCancelled = useRef(false);
  const [value, setValue] = useState<string>(defaultValue);

  const { updateAnnotation } = useAnnotations({
    documentId,
    groupBlockIndex,
  });

  const handleKeyEvent = useKeyEvent(Keys.ESCAPE, () =>
    dispatch(changeEditing(null)),
  );

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setValue(target.value);
  };

  const handleEnter = (event: SyntheticEvent) => {
    event.stopPropagation();
    dispatch(changeEditing(null));
    if (!isEmpty(value) && !isCancelled.current) {
      updateAnnotation(
        annotationId,
        value,
        entityType,
        true,
        isTableMode ? true : entityType.multipleGroupBlocks,
      );
    }
  };

  const onBlur = (event: SyntheticEvent) => {
    handleEnter(event);
    dispatch(
      addBlurredEntity(`${entityType.entityType.id}-${groupBlockIndex}`),
    );
  };

  const handleCancel = (event: SyntheticEvent) => {
    event.stopPropagation();
    isCancelled.current = true;
    dispatch(changeEditing(null));
  };

  const handleClick = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  return {
    value,
    setValue,
    handleChange,
    handleEnter,
    onBlur,
    handleCancel,
    handleClick,
    handleKeyEvent,
    defaultValue,
  };
};

export default useEditEntityText;
